import { useEffect, useRef } from "react";
import { Project, projects, deriveEdges } from "../data/projects";

/* ────────────────────────────────────────────────────────────────
   Port of the observatory's constellation view.
   Source: ~/.claude/observatory/observatory.html  (renderConstellation,
   lines ~1270–1448). 2D Canvas, stars drift with soft-bounce physics,
   layered radial-gradient glow, connection lines based on shared tags,
   twinkling background particles, cross-hair rays on hover.
   ──────────────────────────────────────────────────────────────── */

const STATUS_COLOR: Record<Project["status"], string> = {
  live: "#22c55e",
  beta: "#f59e0b",
  building: "#3b82f6",
  dormant: "#9ca3af",
};

interface Star {
  project: Project;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  phase: number;
  pulseSpeed: number;
}

interface Particle {
  x: number;
  y: number;
  r: number;
  alpha: number;
  twinkleSpeed: number;
  phase: number;
}

interface Connection {
  a: number;
  b: number;
  strength: number;
}

interface Props {
  onSelect: (project: Project) => void;
  selectedId?: string;
  onReady: () => void;
}

export default function Scene3D({ onSelect, selectedId, onReady }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // keep latest props in refs so the single long-lived animation loop
  // can read current values without restarting
  const selectedIdRef = useRef(selectedId);
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    let W = 0;
    let H = 0;
    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Stars — one per project, random position, gentle velocity
    const stars: Star[] = projects.map((p) => ({
      project: p,
      x: Math.random() * Math.max(1, W - 160) + 80,
      y: Math.random() * Math.max(1, H - 160) + 80,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      radius: 8 + Math.random() * 7,
      color: STATUS_COLOR[p.status],
      phase: Math.random() * Math.PI * 2,
      pulseSpeed: 0.012 + Math.random() * 0.02,
    }));

    // Background twinkle particles
    const particles: Particle[] = Array.from({ length: 140 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.2 + 0.3,
      alpha: Math.random() * 0.5 + 0.15,
      twinkleSpeed: 0.005 + Math.random() * 0.02,
      phase: Math.random() * Math.PI * 2,
    }));

    // Connections from shared tags
    const connections: Connection[] = [];
    for (const e of deriveEdges(projects)) {
      const a = projects.findIndex((p) => p.id === e.source);
      const b = projects.findIndex((p) => p.id === e.target);
      if (a >= 0 && b >= 0) {
        connections.push({ a, b, strength: Math.min(e.weight / 3, 1) });
      }
    }

    // Mouse tracking
    let hovered: Star | null = null;
    let mouseX = -1;
    let mouseY = -1;

    const onMove = (e: MouseEvent) => {
      const cr = canvas.getBoundingClientRect();
      mouseX = e.clientX - cr.left;
      mouseY = e.clientY - cr.top;
      hovered = null;
      // iterate reverse so top-drawn stars win
      for (let i = stars.length - 1; i >= 0; i--) {
        const s = stars[i];
        const dx = mouseX - s.x;
        const dy = mouseY - s.y;
        const hit = s.radius + 10;
        if (dx * dx + dy * dy < hit * hit) {
          hovered = s;
          break;
        }
      }
      canvas.style.cursor = hovered ? "pointer" : "default";
    };
    const onLeave = () => {
      mouseX = mouseY = -1;
      hovered = null;
      canvas.style.cursor = "default";
    };
    const onClick = () => {
      if (hovered) onSelectRef.current(hovered.project);
    };

    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    canvas.addEventListener("click", onClick);

    // Signal parent we're mounted (HUD can fade in)
    const readyTimer = window.setTimeout(() => onReady(), 180);

    let raf = 0;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      // Background radial gradient (observatory: #0d0d18 → #060608)
      const bgGrad = ctx.createRadialGradient(
        W / 2,
        H / 2,
        0,
        W / 2,
        H / 2,
        Math.max(W, H) * 0.75
      );
      bgGrad.addColorStop(0, "#0d0d18");
      bgGrad.addColorStop(1, "#060608");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, W, H);

      // Twinkle particles
      for (const p of particles) {
        p.phase += p.twinkleSpeed;
        const alpha = p.alpha * (0.5 + 0.5 * Math.sin(p.phase));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 200, 230, ${alpha})`;
        ctx.fill();
      }

      // Connections
      for (const c of connections) {
        const a = stars[c.a];
        const b = stars[c.b];
        const alpha = c.strength * 0.12;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = `rgba(150, 150, 200, ${alpha})`;
        ctx.lineWidth = 0.5 + c.strength;
        ctx.stroke();
      }

      // Stars
      const selId = selectedIdRef.current;
      for (const star of stars) {
        star.x += star.vx;
        star.y += star.vy;
        // soft boundary bounce
        if (star.x < 60 || star.x > W - 60) star.vx *= -1;
        if (star.y < 60 || star.y > H - 60) star.vy *= -1;

        star.phase += star.pulseSpeed;
        const pulse = 1 + 0.08 * Math.sin(star.phase);
        const r = star.radius * pulse;
        const isHovered = star === hovered;
        const isSelected = star.project.id === selId;
        const accent = isHovered || isSelected;

        // Outer glow
        const glowR = r * (accent ? 4 : 2.5);
        const glow = ctx.createRadialGradient(
          star.x,
          star.y,
          0,
          star.x,
          star.y,
          glowR
        );
        glow.addColorStop(0, star.color + (accent ? "60" : "30"));
        glow.addColorStop(0.4, star.color + "15");
        glow.addColorStop(1, star.color + "00");
        ctx.beginPath();
        ctx.arc(star.x, star.y, glowR, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // Core gradient (white → colour → fading colour)
        const core = ctx.createRadialGradient(
          star.x,
          star.y,
          0,
          star.x,
          star.y,
          r
        );
        core.addColorStop(0, "#ffffff");
        core.addColorStop(0.3, star.color);
        core.addColorStop(1, star.color + "80");
        ctx.beginPath();
        ctx.arc(star.x, star.y, r, 0, Math.PI * 2);
        ctx.fillStyle = core;
        ctx.fill();

        // Bright point
        ctx.beginPath();
        ctx.arc(star.x, star.y, Math.max(1.5, r * 0.2), 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        ctx.fill();

        // Label
        ctx.font = `${accent ? "600" : "500"} ${
          accent ? 13 : 11
        }px Inter, sans-serif`;
        ctx.textAlign = "center";
        ctx.fillStyle = accent ? "#ffffff" : "rgba(200, 200, 220, 0.7)";
        ctx.fillText(star.project.name, star.x, star.y + r + 16);

        // Status tag below, for hover/selected
        if (accent) {
          ctx.font = "500 11px 'JetBrains Mono', monospace";
          ctx.fillStyle = "rgba(200, 200, 220, 0.5)";
          ctx.fillText(star.project.status, star.x, star.y + r + 32);
        }
      }

      // Cross-hair rays on hovered star
      if (hovered) {
        const s = hovered;
        const rayLen = s.radius * 3;
        ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
        ctx.lineWidth = 1;
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 2) {
          ctx.beginPath();
          ctx.moveTo(
            s.x + Math.cos(angle) * s.radius * 1.5,
            s.y + Math.sin(angle) * s.radius * 1.5
          );
          ctx.lineTo(
            s.x + Math.cos(angle) * rayLen,
            s.y + Math.sin(angle) * rayLen
          );
          ctx.stroke();
        }
      }

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(readyTimer);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      canvas.removeEventListener("click", onClick);
    };
    // run once — props are read via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: "100%",
        background: "#060608",
        display: "block",
      }}
    />
  );
}
